import React, { useCallback, useEffect, useRef, useState } from 'react';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';

import { createCatalogRun, getCatalogArtifacts, renameCatalogScript, uploadCatalogScript } from '../../api/catalogs';
import CatalogScriptManager from './CatalogScriptManager';
import CatalogSnapshotManager from './CatalogSnapshotManager';
import './CatalogSync.scss';

const FRESH_SNAPSHOT = 'fresh';

const CatalogSync = ({
  mode = 'sync',
  brandKey,
  BrandIntegration_id,
  integrations,
  latestSnapshot,
  liveScript,
  liveSnapshot,
  runs,
  activeRuns = [],
  artifactRevision = 0,
  isPullingSnapshot,
  onPullSnapshot,
  onArtifactsChanged,
  onCreated
}) => {
  const [artifacts, setArtifacts] = useState(null);
  const [snapshotChoice, setSnapshotChoice] = useState(FRESH_SNAPSHOT);
  const [scriptChoice, setScriptChoice] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [isAutoPublish, setIsAutoPublish] = useState(mode === 'sync');
  const [isUploadingScript, setIsUploadingScript] = useState(false);
  const scriptFileRef = useRef(null);
  const artifactScopeRef = useRef(`${brandKey}:${BrandIntegration_id}`);

  const loadArtifacts = useCallback(() => {
    const integrationId = BrandIntegration_id;
    const scope = `${brandKey}:${integrationId}`;
    return getCatalogArtifacts(brandKey, integrationId)
      .then(response => {
        if (artifactScopeRef.current !== scope) return;
        setArtifacts(response);
        setScriptChoice(current => current || response.scripts.entries.find(entry => entry.isLive)?.fingerprint || response.scripts.entries[0]?.fingerprint || '');
      })
      .catch(loadError => cogoToast.error(loadError, { hideAfter: 10 }));
  }, [brandKey, BrandIntegration_id]);

  useEffect(() => {
    artifactScopeRef.current = `${brandKey}:${BrandIntegration_id}`;
    setArtifacts(null);
    setSnapshotChoice(FRESH_SNAPSHOT);
    setScriptChoice('');
    setActiveAction(null);
    setIsAutoPublish(mode === 'sync');
  }, [brandKey, BrandIntegration_id, mode]);

  useEffect(() => {
    loadArtifacts();
  }, [brandKey, BrandIntegration_id, artifactRevision, loadArtifacts]);

  const refreshArtifacts = () => onArtifactsChanged ? onArtifactsChanged() : loadArtifacts();

  const submitRun = async ({ action, snapshotFingerprint, scriptFingerprint, autoPublish }) => {
    setActiveAction(action);
    try {
      await createCatalogRun(brandKey, {
        BrandIntegration_id,
        mode: action === 'sync' ? 'sync' : 'custom',
        snapshotFingerprint: snapshotFingerprint === FRESH_SNAPSHOT ? null : snapshotFingerprint,
        scriptFingerprint: scriptFingerprint || null,
        autoPublish
      });
      await onCreated();
    } catch (createError) {
      cogoToast.error(createError, { hideAfter: 10 });
    } finally {
      setActiveAction(null);
    }
  };

  const pullSnapshot = async () => {
    setActiveAction('snapshot');
    try {
      await onPullSnapshot();
    } finally {
      setActiveAction(null);
    }
  };

  const uploadScript = async file => {
    if (!file) return;
    setIsUploadingScript(true);
    try {
      const uploaded = await uploadCatalogScript(brandKey, BrandIntegration_id, await file.text());
      setScriptChoice(uploaded.fingerprint);
      await refreshArtifacts();
      cogoToast.success('Script stored and selected.');
    } catch (uploadError) {
      cogoToast.error(uploadError, { hideAfter: 10 });
    } finally {
      setIsUploadingScript(false);
      if (scriptFileRef.current) scriptFileRef.current.value = '';
    }
  };

  const renameScript = async (fingerprint, name) => {
    try {
      await renameCatalogScript(brandKey, BrandIntegration_id, fingerprint, name);
      await refreshArtifacts();
      return true;
    } catch (renameError) {
      cogoToast.error(renameError, { hideAfter: 10 });
      return false;
    }
  };

  const snapshotEntries = artifacts?.snapshots?.entries || [];
  const snapshotOptions = mergeSnapshotOptions({ entries: snapshotEntries, latestSnapshot, liveSnapshot });
  const snapshotChoices = new Set([FRESH_SNAPSHOT, ...snapshotOptions.map(entry => entry.fingerprint)]);
  const snapshotValue = snapshotChoices.has(snapshotChoice) ? snapshotChoice : FRESH_SNAPSHOT;
  const matchedRun = snapshotValue === FRESH_SNAPSHOT ? null : findMatchingRun({ runs, snapshotFingerprint: snapshotValue, scriptFingerprint: scriptChoice });
  const scriptEntries = artifacts?.scripts?.entries || [];
  const liveScriptIsStored = liveScript?.fingerprint && scriptEntries.some(entry => entry.fingerprint === liveScript.fingerprint);
  const syncBlocker = !scriptEntries.length
    ? 'No catalog script is stored yet. Upload one in Versions.'
    : liveScript?.fingerprint && !liveScriptIsStored
      ? "The live script isn't in this brand's script library yet. Re-upload it in Versions."
      : !liveScript?.fingerprint && scriptEntries.length > 1
        ? 'There is no live script and more than one stored script. Create a custom version with the script you want first.'
        : null;
  const activeVersionRun = activeRuns.find(run => run.kind !== 'snapshot');
  const activeSnapshotRun = activeRuns.find(run => run.kind === 'snapshot');
  const isVersionBusy = Boolean(activeAction && activeAction !== 'snapshot') || Boolean(activeVersionRun);
  const isSnapshotBusy = activeAction === 'snapshot' || isPullingSnapshot || Boolean(activeSnapshotRun);
  const versionProgress = activeVersionRun ? describeRunProgress(activeVersionRun) : null;
  const snapshotProgress = activeSnapshotRun ? describeRunProgress(activeSnapshotRun) : null;
  const syncLabel = activeAction === 'sync' ? 'Starting sync' : versionProgress || 'Sync now';
  const createLabel = activeAction === 'custom' ? 'Starting version' : versionProgress || 'Create version';

  const isSyncMode = mode === 'sync';

  return (
    <div className='catalogs-sync'>
      {isSyncMode ? (
        <div className='catalogs-sync-actions'>
          <label className='catalogs-sync-auto'>
            <input
              checked={isAutoPublish}
              disabled={isVersionBusy || !artifacts || Boolean(syncBlocker)}
              type='checkbox'
              onChange={event => setIsAutoPublish(event.target.checked)}
            />
            Auto-publish if checks pass
          </label>
          <span className='catalogs-sync-tipwrap' data-tip={syncBlocker || undefined}>
            <button
              className='catalogs-button small catalogs-sync-now'
              disabled={isVersionBusy || !artifacts || Boolean(syncBlocker)}
              onClick={() => submitRun({ action: 'sync', scriptFingerprint: null, snapshotFingerprint: FRESH_SNAPSHOT, autoPublish: isAutoPublish })}
            >
              {activeAction === 'sync' || activeVersionRun ? <LoadingLabel>{syncLabel}</LoadingLabel> : syncLabel}
            </button>
          </span>
        </div>
      ) : (
        <>
          <CatalogSnapshotManager
            brandKey={brandKey}
            BrandIntegration_id={BrandIntegration_id}
            entries={snapshotEntries}
            total={artifacts?.snapshots?.total || 0}
            isLoading={!artifacts}
            isPulling={isSnapshotBusy}
            pullLabel={snapshotProgress || 'Pulling snapshot'}
            onPull={pullSnapshot}
          />
          <CatalogScriptManager
            brandKey={brandKey}
            BrandIntegration_id={BrandIntegration_id}
            entries={scriptEntries}
            integrations={integrations}
            isLoading={!artifacts}
            isUploading={isUploadingScript}
            onRename={renameScript}
            onUpload={() => scriptFileRef.current?.click()}
          />
          <div className='catalogs-custom-version'>
            <h2>Create custom version</h2>
            <div className='catalogs-custom-version-content'>
              <div className='catalogs-sync-custom'>
                <label className='catalogs-sync-field'>
                  <b>Snapshot</b>
                  <select aria-label='Version snapshot' disabled={isVersionBusy} value={snapshotValue} onChange={event => setSnapshotChoice(event.target.value)}>
                    <option value={FRESH_SNAPSHOT}>New snapshot (reuse one pulled in the last 10 minutes)</option>
                    {snapshotOptions.map(entry => (
                      <option key={entry.fingerprint} value={entry.fingerprint}>
                        {snapshotOptionLabel({ entry, latestSnapshot, liveSnapshot })}
                      </option>
                    ))}
                  </select>
                </label>
                <label className='catalogs-sync-field'>
                  <b>Script</b>
                  <select aria-label='Version script' disabled={isVersionBusy || !scriptEntries.length} value={scriptChoice} onChange={event => setScriptChoice(event.target.value)}>
                    {!scriptEntries.length && <option value=''>{artifacts ? 'No scripts stored' : 'Loading…'}</option>}
                    {scriptEntries.map(entry => (
                      <option key={entry.fingerprint} value={entry.fingerprint}>{scriptOptionLabel(entry)}</option>
                    ))}
                  </select>
                </label>
                <label className='catalogs-custom-version-auto'>
                  <input
                    checked={isAutoPublish}
                    disabled={isVersionBusy}
                    type='checkbox'
                    onChange={event => setIsAutoPublish(event.target.checked)}
                  />
                  Auto-publish if checks pass
                </label>
                <button
                  className='catalogs-button compact catalogs-sync-submit'
                  disabled={isVersionBusy || !scriptChoice}
                  title={scriptChoice ? undefined : 'Upload or choose a script first.'}
                  onClick={() => submitRun({ action: 'custom', scriptFingerprint: scriptChoice, snapshotFingerprint: snapshotValue, autoPublish: isAutoPublish })}
                >
                  {activeAction === 'custom' || activeVersionRun ? <LoadingLabel>{createLabel}</LoadingLabel> : createLabel}
                </button>
              </div>
              {matchedRun?.number && (
                <span className='catalogs-sync-match'>
                  {`Same inputs as version #${matchedRun.number} (${String(matchedRun.status).replaceAll('_', ' ')})`}
                </span>
              )}
            </div>
          </div>
        </>
      )}
      <input hidden accept='.py,text/x-python' ref={scriptFileRef} type='file' onChange={event => uploadScript(event.target.files?.[0])} />
    </div>
  );
};

const STEP_LABELS = {
  snapshot: {
    fetching_shopify: 'Pulling snapshot',
    storing_snapshot: 'Storing snapshot',
    default: 'Preparing snapshot'
  },
  build: {
    resolving_script: 'Selecting script',
    running_script: 'Building version',
    validating: 'Validating version',
    storing_version: 'Saving version',
    default: 'Building version'
  },
  checks: { default: 'Checking changes' },
  publish: {
    applying_writes: 'Publishing catalog',
    mysql_applied: 'Finishing publish',
    side_effects_applied: 'Activating catalog',
    integration_activated: 'Recording live version',
    default: 'Publishing'
  }
};

const describeRunProgress = run => {
  const requestedSteps = Object.values(run.steps || {}).filter(step => step.status !== 'not_requested');
  const step = run.currentStep || (run.status === 'publishing' ? 'publish' : null);
  if (!step) return run.status === 'queued' ? 'Queued' : 'Processing';
  const stepState = run.steps?.[step] || {};
  const label = STEP_LABELS[step]?.[stepState.stage] || STEP_LABELS[step]?.default || 'Processing';
  const position = stepState.position || requestedSteps.findIndex(candidate => candidate === stepState) + 1;
  return position > 0 && requestedSteps.length ? `Step ${position} of ${requestedSteps.length} · ${label}` : label;
};

const findMatchingRun = ({ runs, snapshotFingerprint, scriptFingerprint }) => {
  if (!snapshotFingerprint || !scriptFingerprint) return null;
  const matches = (runs || []).filter(run => run.number && run.snapshot?.fingerprint === snapshotFingerprint && run.script?.fingerprint === scriptFingerprint);
  return matches.find(run => run.role === 'live') || matches[0] || null;
};

const mergeSnapshotOptions = ({ entries, latestSnapshot, liveSnapshot }) => {
  const options = [...entries];
  [latestSnapshot, liveSnapshot].filter(Boolean).forEach(snapshot => {
    if (!options.some(entry => entry.fingerprint === snapshot.fingerprint)) options.push(snapshot);
  });
  return options;
};

const snapshotOptionLabel = ({ entry, latestSnapshot, liveSnapshot }) => {
  const isLatest = entry.fingerprint === latestSnapshot?.fingerprint;
  const isLive = entry.fingerprint === liveSnapshot?.fingerprint;
  const prefix = isLatest && isLive ? 'Latest and live' : isLatest ? 'Latest pulled' : isLive ? 'Live' : 'Stored';
  const stamp = formatStamp(entry.generatedAt || entry.lastModifiedAt);
  const details = entry.productCount != null ? `${stamp} · ${Number(entry.productCount).toLocaleString()} products` : `${stamp} · ${entry.fingerprint.slice(0, 8)}`;
  return `${prefix} — ${details}`;
};

const scriptOptionLabel = entry => {
  const uploaded = entry.uploadedAt && `Uploaded ${formatStamp(entry.uploadedAt)}`;
  return [entry.isLive && 'Live', entry.name || entry.fingerprint.slice(0, 8), uploaded].filter(Boolean).join(' · ');
};

const formatStamp = value => (value ? moment(value).format('MMM D, h:mm A') : '—');

const LoadingLabel = ({ children }) => <><FontAwesomeIcon className='catalogs-loading-icon' icon={faSpinner} spin />{children}</>;

export default CatalogSync;
